import { Item3D } from '../types/optimization'
import { adrReferenceService } from '../services/adrReference.service'

export interface ItemAdvancedFormValues {
  dangerous_goods: boolean
  adr_class: string
  un_number: string
  unloading_order: string
  must_be_on_floor: boolean
  keep_together_group: string
  // ── Palettisation ──────────────────────────────────────────────────────
  requires_pallet: boolean
  pallet_type: 'EUR' | 'EUR2' | 'CUSTOM'
  max_pallet_height_mm: string
  pallet_custom_length: string
  pallet_custom_width: string
  max_items_per_pallet: string
  pallet_board_height_mm: string
  pallet_empty_weight_kg: string
  pallet_color: string
  max_pallet_weight_kg: string
}

// Fallback ADR class options (used if API fails)
const FALLBACK_ADR_CLASS_OPTIONS = [
  { value: '1', label: '1 — Explosifs' },
  { value: '2', label: '2 — Gaz' },
  { value: '2.1', label: '2.1 — Gaz inflammables' },
  { value: '2.2', label: '2.2 — Gaz non inflammables, non toxiques' },
  { value: '2.3', label: '2.3 — Gaz toxiques' },
  { value: '3', label: '3 — Liquides inflammables' },
  { value: '4', label: '4 — Solides inflammables' },
  { value: '4.1', label: '4.1 — Solides inflammables' },
  { value: '4.2', label: '4.2 — Spontanément combustibles' },
  { value: '4.3', label: '4.3 — Réagissant avec l\'eau' },
  { value: '5', label: '5 — Comburants' },
  { value: '5.1', label: '5.1 — Comburants' },
  { value: '5.2', label: '5.2 — Peroxydes organiques' },
  { value: '6', label: '6 — Toxiques' },
  { value: '6.1', label: '6.1 — Toxiques' },
  { value: '6.2', label: '6.2 — Infectieux' },
  { value: '7', label: '7 — Radioactifs' },
  { value: '8', label: '8 — Corrosifs' },
  { value: '9', label: '9 — Divers' },
] as const

/**
 * Get ADR class options from API or fallback
 * This function loads ADR classes from the reference data API
 */
export async function getADRClassOptions(): Promise<{ value: string; label: string }[]> {
  try {
    const options = await adrReferenceService.getADRClassOptions()
    return options
  } catch (error) {
    console.warn('Failed to load ADR classes from API, using fallback:', error)
    return FALLBACK_ADR_CLASS_OPTIONS as any
  }
}

// Keep the export for backward compatibility (synchronous fallback)
export const ADR_CLASS_OPTIONS = FALLBACK_ADR_CLASS_OPTIONS

export function emptyItemAdvancedValues(): ItemAdvancedFormValues {
  return {
    dangerous_goods: false,
    adr_class: '',
    un_number: '',
    unloading_order: '',
    must_be_on_floor: false,
    keep_together_group: '',
    requires_pallet: false,
    pallet_type: 'EUR',
    max_pallet_height_mm: '',
    pallet_custom_length: '',
    pallet_custom_width: '',
    max_items_per_pallet: '',
    pallet_board_height_mm: '',
    pallet_empty_weight_kg: '',
    pallet_color: '',
    max_pallet_weight_kg: '',
  }
}

export function validateItemAdvancedFields(advanced: ItemAdvancedFormValues): string | null {
  if (advanced.dangerous_goods) {
    if (!advanced.adr_class.trim()) {
      return 'La classe ADR est requise pour les marchandises dangereuses'
    }
    if (!advanced.un_number.trim()) {
      return 'Le numéro UN est requis pour les marchandises dangereuses'
    }
  }
  if (advanced.unloading_order.trim()) {
    const order = parseInt(advanced.unloading_order, 10)
    if (Number.isNaN(order) || order < 1) {
      return "L'ordre de déchargement doit être un entier positif"
    }
  }
  if (advanced.requires_pallet) {
    if (advanced.pallet_type === 'CUSTOM') {
      const l = parseFloat(advanced.pallet_custom_length)
      const w = parseFloat(advanced.pallet_custom_width)
      if (!advanced.pallet_custom_length.trim() || Number.isNaN(l) || l <= 0) {
        return 'La longueur de la palette personnalisée est requise'
      }
      if (!advanced.pallet_custom_width.trim() || Number.isNaN(w) || w <= 0) {
        return 'La largeur de la palette personnalisée est requise'
      }
    }
    if (advanced.max_pallet_height_mm.trim()) {
      const h = parseFloat(advanced.max_pallet_height_mm)
      if (Number.isNaN(h) || h <= 0) {
        return 'La hauteur maximale de palette doit être un nombre positif'
      }
    }
    if (advanced.max_items_per_pallet.trim()) {
      const n = parseInt(advanced.max_items_per_pallet, 10)
      if (Number.isNaN(n) || n < 0) {
        return 'Le nombre max de colis par palette doit être un entier positif ou 0'
      }
    }
    if (advanced.pallet_board_height_mm.trim()) {
      const h = parseFloat(advanced.pallet_board_height_mm)
      if (Number.isNaN(h) || h <= 0) {
        return 'La hauteur du plateau palette doit être un nombre positif'
      }
    }
    if (advanced.pallet_empty_weight_kg.trim()) {
      const w = parseFloat(advanced.pallet_empty_weight_kg)
      if (Number.isNaN(w) || w < 0) {
        return 'Le poids de la palette vide doit être un nombre positif'
      }
    }
    if (advanced.pallet_color.trim()) {
      const color = advanced.pallet_color.trim()
      if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
        return 'La couleur de la palette doit être au format hex #RRGGBB'
      }
    }
    if (advanced.max_pallet_weight_kg.trim()) {
      const w = parseFloat(advanced.max_pallet_weight_kg)
      if (Number.isNaN(w) || w <= 0) {
        return 'Le poids max de la palette chargée doit être un nombre positif'
      }
    }
  }
  return null
}

export function applyAdvancedFieldsToItem(item: Item3D, advanced: ItemAdvancedFormValues): Item3D {
  const result: Item3D = { ...item }

  if (advanced.dangerous_goods) {
    result.dangerous_goods = true
    result.is_hazardous = true
    result.adr_class = advanced.adr_class.trim()
    result.un_number = advanced.un_number.trim()
  }

  if (advanced.unloading_order.trim()) {
    result.unloading_order = parseInt(advanced.unloading_order, 10)
  }

  if (advanced.must_be_on_floor) {
    result.must_be_on_floor = true
  }

  if (advanced.keep_together_group.trim()) {
    result.keep_together_group = advanced.keep_together_group.trim()
  }

  // ── Palettisation ────────────────────────────────────────────────────
  if (advanced.requires_pallet) {
    result.requires_pallet = true
    result.pallet_type = advanced.pallet_type
    if (advanced.max_pallet_height_mm.trim()) {
      result.max_pallet_height_mm = parseFloat(advanced.max_pallet_height_mm)
    }
    if (advanced.pallet_type === 'CUSTOM') {
      result.pallet_custom_length = parseFloat(advanced.pallet_custom_length) || null
      result.pallet_custom_width = parseFloat(advanced.pallet_custom_width) || null
    }
    if (advanced.max_items_per_pallet.trim()) {
      result.max_items_per_pallet = parseInt(advanced.max_items_per_pallet, 10)
    }
    if (advanced.pallet_board_height_mm.trim()) {
      result.pallet_board_height_mm = parseFloat(advanced.pallet_board_height_mm)
    }
    if (advanced.pallet_empty_weight_kg.trim()) {
      result.pallet_empty_weight_kg = parseFloat(advanced.pallet_empty_weight_kg)
    }
    if (advanced.pallet_color.trim()) {
      result.pallet_color = advanced.pallet_color.trim()
    }
    if (advanced.max_pallet_weight_kg.trim()) {
      result.max_pallet_weight_kg = parseFloat(advanced.max_pallet_weight_kg)
    }
  }

  return result
}

export function formatItemAdvancedTags(item: Item3D): string[] {
  const tags: string[] = []
  if (item.dangerous_goods || item.is_hazardous) {
    const adr = item.adr_class != null ? `ADR ${item.adr_class}` : 'ADR'
    const un = item.un_number ? ` (${item.un_number})` : ''
    tags.push(`${adr}${un}`)
  }
  if (typeof item.unloading_order === 'number') {
    tags.push(`Déchargement #${item.unloading_order}`)
  }
  if (item.must_be_on_floor) {
    tags.push('Au sol uniquement')
  }
  if (item.keep_together_group) {
    tags.push(`Groupe: ${item.keep_together_group}`)
  }
  if (item.requires_pallet) {
    const type = item.pallet_type || 'EUR'
    tags.push(`Palette ${type}`)
  }
  if (item.is_palletized) {
    tags.push(`📦 Palette ${item.pallet_type || 'EUR'}`)
  }
  return tags
}
